import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useCurrentUser } from '../account/useAuth';
import { BrandLockup } from '../branding/branding';
import { AdminGlobalSearchBar } from './GlobalSearchBar';
import './AdminShell.css';

/**
 * Top-level chrome for the admin console.
 *
 * Renders a left rail of navigation entries + a content slot
 * (`<Outlet />`). Sub-routes plug their pages into the slot. The rail
 * collapses to a horizontal tab bar under 720px so the console stays
 * usable on a tablet / phone.
 *
 * Permission: admin only. Anyone else gets a friendly "you don't have
 * access" panel instead of a 403 page so the route doesn't look
 * broken — same posture the rest of the app takes for sign-in
 * required surfaces.
 */

interface NavItem {
  to: string;
  label: string;
  icon: string;
  /** When set, only show this item when the env enables it. */
  end?: boolean;
}

const NAV: ReadonlyArray<NavItem> = [
  { to: '/admin', label: 'Overview', icon: '🏠', end: true },
  { to: '/admin/users', label: 'Users', icon: '👥' },
  { to: '/admin/users?suspended=true', label: 'Suspended users', icon: '⛔' },
  { to: '/admin/classrooms', label: 'Classrooms', icon: '🏫' },
  { to: '/admin/submissions', label: 'Submissions', icon: '🛡️' },
  { to: '/admin/published-apps', label: 'Published apps', icon: '🚀' },
  { to: '/admin/reports', label: 'Reports', icon: '🚨' },
  { to: '/admin/messages', label: 'Inbox', icon: '📨' },
  { to: '/admin/gallery', label: 'Gallery', icon: '🖼️' },
  { to: '/admin/audit-log', label: 'Activity log', icon: '📜' },
  { to: '/admin/moderation-audit', label: 'Moderation log', icon: '🔍' },
  { to: '/admin/config', label: 'Configuration', icon: '⚙️' },
  { to: '/admin/email-outbox', label: 'Email outbox', icon: '✉️' },
  { to: '/admin/health', label: 'System health', icon: '❤️' },
  { to: '/admin/flags', label: 'Feature flags', icon: '🚩' },
];

export function AdminShell(): JSX.Element {
  const { data: user, isPending } = useCurrentUser();
  const location = useLocation();
  if (isPending) {
    return (
      <main className="bl-admin">
        <p className="bl-admin__notice">Loading…</p>
      </main>
    );
  }
  if (!user) {
    return (
      <main className="bl-admin">
        <p className="bl-admin__notice">Sign in with an admin account to use this console.</p>
      </main>
    );
  }
  if (user.role !== 'admin') {
    return (
      <main className="bl-admin">
        <p className="bl-admin__notice">
          This console is for administrators. Your account is signed in as{' '}
          <strong>{user.role}</strong>; ask an admin to promote you if you need access.
        </p>
      </main>
    );
  }
  return (
    <main className="bl-admin">
      <header className="bl-admin__topbar">
        <div className="bl-admin__brand">
          <BrandLockup subBrand="Admin" />
        </div>
        <AdminGlobalSearchBar />
        <div className="bl-admin__who" aria-label="Signed-in admin">
          <span className="bl-admin__role">admin</span>
          <span>{user.email}</span>
        </div>
      </header>
      <div className="bl-admin__body">
        <nav className="bl-admin__nav" aria-label="Admin sections" data-current={location.pathname}>
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `bl-admin__nav-link${isActive ? ' bl-admin__nav-link--active' : ''}`
              }
            >
              <span aria-hidden="true">{item.icon}</span>
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>
        <section className="bl-admin__content">
          <Outlet />
        </section>
      </div>
    </main>
  );
}
